import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Network, Layout, Link as LinkIcon, Users, Shield, TrendingUp } from 'lucide-react';
import Spline from '@splinetool/react-spline';
import { aiAgents, agentCategories, industrySolutions, platformFeatures } from '../agentData';
import { getSettingsSync } from '../utils/settings';
import './NewHome.css';

const NewHome = () => {
  // Get first 8 featured agents
  const featuredAgents = aiAgents.slice(0, 8);
  const [splineError, setSplineError] = useState(false);
  const [settings, setSettings] = useState(getSettingsSync());

  useEffect(() => {
    const loadSettings = async () => {
      const { getSettings } = await import('../utils/settings');
      const fetchedSettings = await getSettings();
      setSettings(fetchedSettings);
    };
    loadSettings();
    
    const handleSettingsUpdate = () => {
      loadSettings();
    };
    
    window.addEventListener('settingsUpdated', handleSettingsUpdate);
    return () => window.removeEventListener('settingsUpdated', handleSettingsUpdate);
  }, []);

  const handleSplineLoad = (spline) => {
    console.log('Spline scene loaded successfully');
  };

  const handleSplineError = (error) => {
    console.error('Spline loading error:', error);
    setSplineError(true);
  };

  return (
    <div className="new-home-page">
      {/* Hero Section */}
      <section className="hero-section-new">
        <div className="hero-content-new">
          <h1 className="display-huge">{settings.homeHeroTitle}</h1>
          <p className="body-large" style={{ marginTop: '32px', maxWidth: '900px', margin: '32px auto 0' }}>
            {settings.homeHeroDescription}
          </p>
          <div className="hero-buttons">
            <Link to="/demo" className="btn-primary">
              Request Access <ArrowRight size={20} />
            </Link>
            <Link to="/agents" className="btn-secondary">
              Explore AI Agents
            </Link>
          </div>
          <p className="body-small" style={{ marginTop: '40px', color: 'var(--text-muted)' }}>
            Trusted by enterprises and scale-ups worldwide
          </p>
          <div className="spline-container-hero">
            {!splineError ? (
              <Spline 
                scene="https://prod.spline.design/NbVmy6DPLhY-5Lvg/scene.splinecode"
                onLoad={handleSplineLoad}
                onError={handleSplineError}
              />
            ) : (
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '100%',
                color: 'var(--text-muted)',
                fontSize: '14px'
              }}>
                3D Animation
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Industry Solutions */}
      <section className="industries-section-new">
        <div className="section-header">
          <h2 className="display-large" style={{ textAlign: 'center' }}>Industry Specific AI: One Platform for All Your Needs</h2>
          <p className="body-medium" style={{ marginTop: '24px', maxWidth: '900px', margin: '24px auto 0' }}>
            Explore how AI delivers tangible results in diverse scenarios. From optimizing workflows to driving innovation, see how our technology addresses specific challenges and enhances performance across various fields.
          </p>
        </div>

        <div className="industry-solutions-grid">
          {industrySolutions.map((industry) => (
            <Link
              key={industry.id}
              to={`/solutions/${industry.id}`}
              className="industry-solution-card dark-hover"
            >
              <div className="industry-solution-image" style={{ backgroundImage: `url(${industry.image})` }}></div>
              <div className="industry-solution-content">
                <h3 className="heading-2">{industry.name}</h3>
                <p className="body-medium" style={{ marginTop: '16px' }}>{industry.description}</p>
                <div className="benefits-list" style={{ marginTop: '20px' }}>
                  {industry.benefits.slice(0, 2).map((benefit, idx) => (
                    <div key={idx} className="body-small" style={{ display: 'flex', alignItems: 'start', gap: '8px', marginTop: '8px' }}>
                      <span style={{ color: 'var(--brand-primary)' }}>✓</span>
                      {benefit}
                    </div>
                  ))}
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '60px' }}>
          <Link to="/solutions" className="btn-secondary">
            View All Solutions <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* AI Agents Section */}
      <section className="agents-preview-section">
        <div className="section-header">
          <h2 className="display-large">AI Agents: Reliable Workforce With Human-Level Performance</h2>
          <p className="body-medium" style={{ marginTop: '24px', maxWidth: '900px', margin: '24px auto 0' }}>
            AI agents deliver precise, efficient task automation, matching human-level performance. They operate continuously, reducing errors and increasing productivity, allowing businesses to scale fast without additional human resource costs.
          </p>
        </div>

        <div className="agents-grid-new">
          {featuredAgents.map((agent) => (
            <div
              key={agent.id}
              className="agent-card-new dark-hover"
            >
              <div className="agent-card-image" style={{ backgroundImage: `url(${agent.image})` }}></div>
              <div className="agent-card-content">
                <h3 className="heading-3">{agent.name}</h3>
                <p className="body-small category-tag">{agentCategories.find(cat => cat.id === agent.category)?.name}</p>
                <div className="workflow-tags">
                  {agent.workflows.slice(0, 2).map((workflow, idx) => (
                    <span key={idx} className="workflow-tag body-small">{workflow}</span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: '60px' }}>
          <Link to="/agents" className="btn-secondary">
            See All Agents ({aiAgents.length}+) <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Platform Section */}
      <section className="platform-section">
        <div className="platform-header">
          <span className="platform-badge">AI Platform</span>
          <h2 className="display-large" style={{ marginTop: '24px' }}>Agentic. Reliable. Accurate AI for Enterprise</h2>
          <p className="body-medium" style={{ marginTop: '24px', maxWidth: '800px', margin: '24px auto 0' }}>
            An AI-native platform that combines accuracy, reliability, and flexibility in one powerful solution. With multi-agent capabilities and a modular design, it acts as the glue between your systems.
          </p>
          <Link to="/platform" className="btn-secondary" style={{ marginTop: '40px' }}>
            Explore our platform <ArrowRight size={20} />
          </Link>
        </div>

        <div className="platform-features-grid">
          {platformFeatures.map((feature, idx) => {
            const IconComponent = {
              'Network': Network,
              'Layout': Layout,
              'Link': LinkIcon,
              'Users': Users,
              'Shield': Shield,
              'TrendingUp': TrendingUp
            }[feature.icon];

            return (
              <div key={idx} className="platform-feature-card">
                <IconComponent size={32} color="var(--brand-primary)" />
                <h3 className="heading-3" style={{ marginTop: '20px' }}>{feature.title}</h3>
                <p className="body-small" style={{ marginTop: '12px' }}>{feature.description}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <h2 className="display-large" style={{ textAlign: 'center' }}>Improve Operational Efficiency and Create Leverage. Fast.</h2>
        <p className="body-medium" style={{ textAlign: 'center', marginTop: '24px', maxWidth: '900px', margin: '24px auto 0' }}>
          Deploy AI agents to streamline operations and amplify your business's efficiency. These agents optimize processes, reduce delays, and enhance output, ensuring you gain a competitive edge with speed and precision.
        </p>

        <div className="benefits-grid">
          <div className="benefit-card">
            <h3 className="display-medium" style={{ color: 'var(--brand-primary)' }}>63%</h3>
            <p className="body-medium" style={{ marginTop: '16px' }}>Average cost reduction</p>
          </div>
          <div className="benefit-card">
            <h3 className="display-medium" style={{ color: 'var(--brand-primary)' }}>10x</h3>
            <p className="body-medium" style={{ marginTop: '16px' }}>Faster process execution</p>
          </div>
          <div className="benefit-card">
            <h3 className="display-medium" style={{ color: 'var(--brand-primary)' }}>24/7</h3>
            <p className="body-medium" style={{ marginTop: '16px' }}>Continuous operation</p>
          </div>
          <div className="benefit-card">
            <h3 className="display-medium" style={{ color: 'var(--brand-primary)' }}>99%</h3>
            <p className="body-medium" style={{ marginTop: '16px' }}>Accuracy rate</p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section-new">
        <h2 className="display-large\">Build Your AI Workforce Today</h2>
        <p className="body-large" style={{ marginTop: '24px' }}>
          With EAET, build an AI workforce that automates tasks efficiently. Deploy intelligent agents to streamline operations and improve performance across your business today!
        </p>
        <div style={{ marginTop: '40px' }}>
          <Link to="/demo" className="btn-primary">
            Request Access <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default NewHome;
